import { Injectable, ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../components/toast/toast.component';
import { Toast } from '../../models/toast.model';

/**
 * Service to manage and display global toasts.
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private _appRef = inject(ApplicationRef);
  private _injector = inject(EnvironmentInjector);
  private componentRef?: ComponentRef<ToastComponent>;

  private ensureComponentExists(): void {
    if (!this.componentRef) {
      // Create the component
      this.componentRef = createComponent(ToastComponent, {
        environmentInjector: this._injector
      });

      // Attach to the application view
      this._appRef.attachView(this.componentRef.hostView);

      // Append to the DOM
      const domElem = (this.componentRef.hostView as any).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);
    }
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  /**
   * Shows a generic toast.
   * @param {Omit<Toast, 'id'>} toast Toast config.
   */
  showToast(toast: Omit<Toast, 'id'>): void {
    this.ensureComponentExists();

    const newToast: Toast = {
      ...toast,
      id: this.generateId(),
      duration: toast.duration || 4000
    };

    this.componentRef!.instance.addToast(newToast);
  }

  /**
   * Shows a success toast.
   * @param {string} title Title.
   * @param {string} [message] Body message.
   * @param {number} [duration] Duration in ms.
   */
  success(title: string, message?: string, duration?: number): void {
    this.showToast({ type: 'success', title, message, duration });
  }

  /**
   * Shows an error toast.
   * @param {string} title Title.
   * @param {string} [message] Body message.
   * @param {boolean} [persistent] If true, won't auto-dismiss.
   */
  error(title: string, message?: string, persistent?: boolean): void {
    this.showToast({ type: 'error', title, message, persistent });
  }

  /**
   * Shows a warning toast.
   * @param {string} title Title.
   * @param {string} [message] Body message.
   * @param {number} [duration] Duration in ms.
   */
  warning(title: string, message?: string, duration?: number): void {
    this.showToast({ type: 'warning', title, message, duration });
  }

  /**
   * Shows an info toast.
   * @param {string} title Title.
   * @param {string} [message] Body message.
   * @param {number} [duration] Duration in ms.
   */
  info(title: string, message?: string, duration?: number): void {
    this.showToast({ type: 'info', title, message, duration });
  }

  /**
   * Extracts the backend's `detail` message from an HTTP error response, falling back to a generic message.
   * @param {HttpErrorResponse | unknown} error The error caught from an HTTP request.
   * @param {string} fallback Message to use when the backend didn't return a `detail` field.
   */
  getErrorDetail(error: HttpErrorResponse | unknown, fallback: string): string {
    const detail = (error as HttpErrorResponse)?.error?.detail;
    return typeof detail === 'string' && detail.trim() ? detail : fallback;
  }

  /**
   * Clears all toasts.
   */
  clear(): void {
    if (this.componentRef) {
      this.componentRef.instance.toasts.length = 0;
    }
  }

  /**
   * Destroys the toast component.
   */
  destroy(): void {
    if (this.componentRef) {
      this._appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = undefined;
    }
  }
}